import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  AlertTriangle, Users, Clock, Shield, Heart, 
  FileText, Calendar, Download, Bell, Phone,
  Play, Pause, Mic, Volume2, Star, CheckSquare
} from 'lucide-react';

const RemedyCouncilSystem = ({ onNavigateHome, currentUser }) => {
  const [activeCouncil, setActiveCouncil] = useState(null);
  const [councilRequests, setCouncilRequests] = useState([]);
  const [showSOSButton, setShowSOSButton] = useState(false);
  const [meetingPhase, setMeetingPhase] = useState('pre_council');

  // Remedy Council phases based on your real-world framework
  const councilPhases = [
    { id: 'ritual_opening', name: 'Ritual Opening', duration: 2, description: 'Bell ring, silence, air-aroma breath' },
    { id: 'set_intention', name: 'Set Intention', duration: 1, description: 'Child-safety and minimal win goal' },
    { id: 'talking_stick', name: 'Talking Stick Round', duration: 8, description: 'Uninterrupted sharing (2 mins each)' },
    { id: 'clarifying', name: 'Clarifying Questions', duration: 8, description: 'Non-judgmental facilitator questions' },
    { id: 'heart_witness', name: 'Heart Witness', duration: 5, description: 'What each wants for child/relationship' },
    { id: 'micro_repair', name: 'Micro-Repair', duration: 10, description: 'Apologies, agreements, calendar pledges' },
    { id: 'somatic_reset', name: 'Sing/Somatic Reset', duration: 5, description: 'Forced song or humming before commitments' },
    { id: 'commitments', name: 'Commitments & Monitoring', duration: 5, description: 'Actions, monitor assignments, calendar' },
    { id: 'close', name: 'Close', duration: 1, description: 'Bell, pledge photo, digital signature' }
  ];

  // SOS Request System
  const triggerSOS = () => {
    const sosRequest = {
      id: `sos_${Date.now()}`,
      type: 'family_emergency',
      timestamp: new Date(),
      requestor: currentUser?.name || 'Anonymous',
      status: 'urgent',
      child_involved: true,
      safety_concerns: 'Immediate intervention needed'
    };
    
    setCouncilRequests(prev => [sosRequest, ...prev]);
    
    // Immediate response
    alert(`🚨 SOS ACTIVATED\n\nRemedy Council emergency request sent.\nAssigned facilitators will be notified immediately.\n\nIf this involves child safety, emergency protocols are now active.`);
  };

  // Create Council Request
  const createCouncilRequest = (formData) => {
    const newRequest = {
      id: `council_${Date.now()}`,
      type: 'standard_council',
      issue: formData.issue,
      desired_outcome: formData.desired_outcome,
      children_involved: formData.children_involved,
      safety_concerns: formData.safety_concerns,
      preferred_facilitators: formData.preferred_facilitators,
      requestor: currentUser?.name || 'Anonymous',
      timestamp: new Date(),
      status: 'pending_facilitators',
      auto_triage: formData.children_involved ? 'maternal_presence_required' : 'standard_process'
    };
    
    setCouncilRequests(prev => [newRequest, ...prev]);
    setActiveCouncil(newRequest);
  };

  // Generate PDF Protocol
  const generatePDFProtocol = (councilData) => {
    const protocol = {
      title: `Remedy Council Protocol - ${councilData.issue}`,
      date: new Date().toLocaleDateString(),
      participants: councilData.participants || [],
      agreements: councilData.agreements || [],
      follow_up: councilData.follow_up || [],
      safety_measures: councilData.safety_measures || [],
      calendar_events: councilData.calendar_events || []
    };
    
    // Simulate PDF generation
    alert(`📄 Protocol PDF Generated!\n\nTitle: ${protocol.title}\nDate: ${protocol.date}\n\nDownload would include:\n• Agreements & commitments\n• Follow-up schedule\n• Safety protocols\n• Miniature game process for children`);
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_planetaryfed-v3/artifacts/ugnfkky4_ChatGPT%20Image%20Sep%2011%2C%202025%20at%2012_35_28%20AM.jpeg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      <div className="absolute inset-0 bg-black/60" />

      <div className="relative z-10 p-4 md:p-8">
        
        {/* Header with SOS */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white">
            🌀 Remedy Council System
          </h1>
          
          <Button
            onClick={triggerSOS}
            className="bg-red-600 hover:bg-red-700 text-white font-bold"
            size="lg"
          >
            🚨 SOS
          </Button>
        </div>

        {/* Universal Process Template */}
        <Card className="bg-purple-900/40 backdrop-blur-xl border border-purple-400/30 mb-8">
          <CardHeader>
            <CardTitle className="text-white text-xl">
              🌀 Universal Conflict Resolution Process
            </CardTitle>
            <p className="text-purple-200">
              Canonical flow for families, couples, and community healing
            </p>
          </CardHeader>
          <CardContent>
            
            {/* Trigger Conditions */}
            <div className="mb-6">
              <h3 className="text-yellow-300 font-bold mb-3">Trigger Conditions:</h3>
              <div className="space-y-2 text-purple-100">
                <div>• Standard measures have failed</div>
                <div>• Issue involves a child</div>
                <div>• Conflict persists &gt; 2 episodes</div>
                <div>• Any member may request a Remedy Council</div>
              </div>
            </div>

            {/* Meeting Structure */}
            <div className="mb-6">
              <h3 className="text-cyan-300 font-bold mb-3">Meeting Structure (60 min max):</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {councilPhases.map((phase, index) => (
                  <div 
                    key={phase.id}
                    className={`p-3 rounded-lg border ${
                      meetingPhase === phase.id 
                        ? 'bg-green-500/20 border-green-400' 
                        : 'bg-black/20 border-white/20'
                    }`}
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      <Badge className="bg-cyan-500/30 text-cyan-200">
                        {phase.duration} min
                      </Badge>
                      <span className="text-white text-sm font-medium">
                        {index + 1}. {phase.name}
                      </span>
                    </div>
                    <p className="text-white/70 text-xs">
                      {phase.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button
                onClick={() => createCouncilRequest({
                  issue: 'Family Communication',
                  children_involved: true,
                  safety_concerns: 'None',
                  desired_outcome: 'Peace and understanding'
                })}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Users className="w-4 h-4 mr-2" />
                Family Council
              </Button>
              
              <Button
                onClick={() => createCouncilRequest({
                  issue: 'Couple Conflict',
                  children_involved: false,
                  safety_concerns: 'Emotional safety',
                  desired_outcome: 'Relationship repair'
                })}
                className="bg-pink-600 hover:bg-pink-700"
              >
                <Heart className="w-4 h-4 mr-2" />
                Couple Healing
              </Button>
              
              <Button
                onClick={() => generatePDFProtocol(activeCouncil || { issue: 'Sample Protocol' })}
                className="bg-green-600 hover:bg-green-700"
              >
                <Download className="w-4 h-4 mr-2" />
                PDF Protocol
              </Button>
              
              <Button
                onClick={onNavigateHome}
                className="bg-gray-600 hover:bg-gray-700"
              >
                Back to Game
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Active Council Sessions with Messaging */}
        {activeCouncil && (
          <Card className="bg-black/40 backdrop-blur-xl border border-green-400/30 mb-8">
            <CardHeader>
              <CardTitle className="text-green-300">
                🌀 Active Council: {activeCouncil.issue}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                
                {/* Simple Council Chat */}
                <div className="bg-black/20 rounded-lg p-4 max-h-64 overflow-y-auto">
                  <div className="space-y-3">
                    <div className="bg-blue-500/20 rounded p-3">
                      <div className="text-blue-200 font-medium">Council Facilitator</div>
                      <div className="text-white">Welcome to the Remedy Council for "{activeCouncil.issue}". Let's begin with our ritual opening - take a deep breath and feel the air around us.</div>
                    </div>
                  </div>
                </div>
                
                {/* Council Input */}
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Share your thoughts in the council circle..."
                    className="flex-1 p-3 bg-black/30 border border-white/20 rounded text-white"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        // Add council message
                        console.log('Council message:', e.target.value);
                        alert(`Council message: "${e.target.value}" shared in sacred space.`);
                        e.target.value = '';
                      }
                    }}
                  />
                  <Button className="bg-green-600 hover:bg-green-700">
                    <Bell className="w-4 h-4 mr-2" />
                    Ring Bell
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Council Requests */}
        {councilRequests.length > 0 && (
          <Card className="bg-black/40 backdrop-blur-xl border border-red-400/30">
            <CardHeader>
              <CardTitle className="text-red-300">
                🚨 Active Council Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {councilRequests.map(request => (
                  <div 
                    key={request.id}
                    className="p-4 bg-red-900/20 border border-red-400/30 rounded-lg"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="text-white font-bold">{request.issue}</h4>
                      <Badge className={
                        request.type === 'family_emergency' 
                          ? 'bg-red-500/30 text-red-200' 
                          : 'bg-yellow-500/30 text-yellow-200'
                      }>
                        {request.type === 'family_emergency' ? 'SOS' : 'Standard'}
                      </Badge>
                    </div>
                    
                    <div className="text-white/70 text-sm space-y-1">
                      <div>Requestor: {request.requestor}</div>
                      <div>Status: {request.status}</div>
                      {request.children_involved && (
                        <div className="text-yellow-300">⚠️ Child involved - Maternal presence required</div>
                      )}
                    </div>
                    
                    <div className="flex space-x-2 mt-3">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700">
                        Accept as Facilitator
                      </Button>
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                        Monitor Assignment
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RemedyCouncilSystem;